import React, { useContext, useState } from "react";
import { Modal, Descriptions, Tag, Typography, Button } from "antd";
import { listResult } from "../../constants/API/API";
import moment from "moment";
import useApi from "../../hooks/useApi";
import { Severty, ShowToast } from "../../helper/toast";
import { AppStateContext } from "../../context/AppContext";
import CustomTable from "../../components/CustomTable";
import useFilter from "../../hooks/useFilter";

const { Title } = Typography;

const ExamAdmin = () => {
  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Graduation Management");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(false);
  const [selectedKyu] = useState(null);
  const [totalDocs, setTotalDocs] = useState(0);
  const [filters, setFilters] = useFilter({
    search: "",
  });

  const columns = [
    {
      title: "Student",
      dataIndex: ["studentId", "name"],
      key: "student",
      render: (val) => val || "-",
    },
    {
      title: "Student",
      dataIndex: ["studentId", "parentId", "name"],
      key: "student",
      render: (val) => val || "-",
    },
    {
      title: "Next Belt",
      key: "nextBeltId",
      render: (_, { nextBeltId }) => {
        return (
          <>
            <p key={nextBeltId?._id}>{nextBeltId?.name || "-"}</p>
          </>
        );
      },
    },
    {
      title: "Branch",
      dataIndex: ["branchId", "name"],
      key: "branch",
      render: (val) => val || "-",
    },
    {
      title: "Exam",
      key: "exam",
      render: (_, { examId }) => examId?.name || "-",
    },
    {
      title: "Exam Date",
      key: "examDate",
      render: (_, { examId }) =>
        examId?.date ? moment(examId.date).format("ll") : "-",
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      render: (val) => (val != null ? val : "-"),
    },
    {
      title: "Result",
      dataIndex: "result",
      key: "result",
      render: (val) => val || "-",
    },
    {
      title: "Graduation Date",
      key: "graduationDate",
      render: (_, { date, createdAt }) =>
        moment(date || createdAt).format("ll") || "-",
    },

    // {
    //   title: "Actions",
    //   key: "actions",
    //   render: (_, rec) => (
    //     <div className="d-flex align-items-center gap-2">
    //       <Button
    //         onClick={() => {
    //           setView(true);
    //           setSelectedKyu(rec);
    //         }}
    //         className="m-0"
    //       >
    //         View Result
    //       </Button>
    //     </div>
    //   ),
    // },
  ];

  const { loading: isGraduationLoading } = useApi({
    callback: listResult,
    dependencies: [filters],
    payload: filters,
    shouldCallApi: true,
    setLoading,
    onSuccess: ({ data }) => {
      setLoading(false);
      setList(data || []);
      setTotalDocs((data || []).length);
    },
    onError: (err) => {
      setLoading(false);
      ShowToast(err, Severty.ERROR);
    },
  });

  const handleExport = () => {
    if (!list || list.length === 0) {
      ShowToast("No data available to export", Severty.WARNING);
      return;
    }

    const headers = [
      "Student",
      "Parent",
      "Next Belt",
      "Branch",
      "Exam Date",
      "Score",
      "Result",
      "Remarks",
      "Graduated On",
    ];

    const csvData = list.map((record) => [
      record.studentId?.name || "-",
      record.studentId?.parentId?.name || "-",
      record.nextBeltId?.name || "-",
      record.branchId?.name || "-",
      moment(record.examId?.date).format("ll") || "-",
      record.score || "-",
      record.result || "-",
      record.remarks || "-",
      moment(record.createdAt).format("ll") || "-",
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "graduation_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    ShowToast("Report exported successfully", Severty.SUCCESS);
  };

  return (
    <>
      <CustomTable
        data={list}
        loading={loading || !!isGraduationLoading}
        columns={columns}
        filters={filters}
        setFilters={setFilters}
        fields={{ range: false, search: true }}
        searchField={["search"]}
        searchPlaceholder="Search by Student Name"
        customView={
          <Button type="primary" onClick={handleExport}>
            Export Report
          </Button>
        }
        totalDocs={totalDocs || list.length}
      />

      {view && (
        <KyuView
          selectedKyu={selectedKyu}
          setViewVisible={setView}
          viewVisible={view}
        />
      )}
    </>
  );
};

export default ExamAdmin;

const KyuView = ({ viewVisible, setViewVisible, selectedKyu: kyuData }) => {
  console.log(kyuData);
  return (
    <Modal
      open={viewVisible}
      title={
        <Title level={4} style={{ margin: 0 }}>
          Graduation Details
        </Title>
      }
      onCancel={() => setViewVisible(false)}
      footer={null}
      width={600}
      destroyOnClose
    >
      {kyuData && (
        <Descriptions
          bordered
          column={1}
          labelStyle={{
            fontWeight: 500,
            backgroundColor: "#fafafa",
            minWidth: "150px",
          }}
          contentStyle={{
            backgroundColor: "#fff",
          }}
        >
          <Descriptions.Item label="Student Name">
            {kyuData?.studentId?.name}
          </Descriptions.Item>
          <Descriptions.Item label="Current Belt">
            {kyuData?.beltId?.name}
          </Descriptions.Item>
          <Descriptions.Item label="Classes Required">
            {kyuData?.requiredClasses}
          </Descriptions.Item>
          <Descriptions.Item label="Always Eligible">
            {kyuData?.alwaysEligible ? "Yes" : "No"}
          </Descriptions.Item>
          <Descriptions.Item label="Trigers Graduation Eligibility">
            {kyuData?.triggersGraduationEligibility ? "Yes" : "No"}
          </Descriptions.Item>
          <Descriptions.Item label="UID">
            <Tag style={{ fontFamily: "monospace" }}>{kyuData._id}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={kyuData.isActive ? "success" : "error"}>
              {kyuData.isActive ? "Active" : "Inactive"}
            </Tag>
          </Descriptions.Item>
        </Descriptions>
      )}
    </Modal>
  );
};
